/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type {
  INetworkAvailabilityEvaluationListener,
  INetworkAvailabilityService
} from "../../model/interfaces";
import { Log } from "../../model/logging";
import { NetworkResource } from "../../model/enums";
import { resourceAvailabilityChanged } from "./networkAvailabilitySlice";
import type { INetworkResourceAvailability } from "../../model/types";
import type { UnityAppDispatch } from "../UnityAppStore";

/**
 * Bridge that connects the domain-layer NetworkAvailabilityService to the
 * Redux (ViewModel) networkAvailability slice.
 *
 * The bridge implements INetworkAvailabilityEvaluationListener and subscribes
 * only to graph-level settlement notifications. On each settlement, it reads
 * the current availability for every NetworkResource in a single pass and
 * syncs the results into Redux. This avoids per-node subscriptions and ensures
 * Redux state is always updated atomically after the full graph has settled.
 *
 * Lifecycle:
 *  1. Instantiate with a reference to the service and the store dispatch.
 *  2. Call start() to register the evaluation listener and seed initial state.
 *  3. Call stop() to unregister when no longer needed.
 *
 * @see NETWORK_AVAILABILITY_SERVICE_DESIGN.md §14.3 (UI integration)
 */
class NetworkAvailabilityBridge implements INetworkAvailabilityEvaluationListener {
  private static readonly TAG: string = "NetworkAvailabilityBridge";

  /** Reference to the domain-layer network availability service. */
  private readonly mService: INetworkAvailabilityService;

  /** Whether this bridge is currently subscribed to the service. */
  private mIsActive: boolean;

  /** Redux dispatch function used to sync availability into the store. */
  private readonly mDispatch: UnityAppDispatch;

  /**
   * All NetworkResource values, cached to avoid repeated enum iteration.
   */
  private static readonly ALL_RESOURCES: ReadonlyArray<NetworkResource> = [
    NetworkResource.NETWORK_INTERFACE,
    NetworkResource.INTERNET
  ];

  /**
   * Creates a new NetworkAvailabilityBridge.
   * @param service - The domain-layer INetworkAvailabilityService instance
   * @param dispatch - The Redux store dispatch function
   */
  constructor(
    service: INetworkAvailabilityService,
    dispatch: UnityAppDispatch
  ) {
    this.mService = service;
    this.mDispatch = dispatch;
    this.mIsActive = false;
  }

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────────────

  /**
   * Registers this bridge as a graph evaluation listener and seeds
   * the Redux state with the current availability snapshot.
   */
  public start(): void {
    if (this.mIsActive) {
      Log.w(
        NetworkAvailabilityBridge.TAG,
        "start() bridge is already active — ignoring"
      );
      return;
    }

    Log.i(NetworkAvailabilityBridge.TAG, "start() subscribing to service");

    this.mService.addNetworkAvailabilityEvaluationListener(this);

    this.mIsActive = true;

    // Seed Redux state with the current snapshot
    this.syncAllResources();
  }

  /**
   * Unregisters this bridge from the service, stopping all Redux
   * dispatches for network availability changes.
   */
  public stop(): void {
    if (!this.mIsActive) {
      Log.w(
        NetworkAvailabilityBridge.TAG,
        "stop() bridge is not active — ignoring"
      );
      return;
    }

    Log.i(NetworkAvailabilityBridge.TAG, "stop() unsubscribing from service");

    this.mService.removeNetworkAvailabilityEvaluationListener(this);

    this.mIsActive = false;
  }

  // ──────────────────────────────────────────────────────────────────
  // INetworkAvailabilityEvaluationListener
  // ──────────────────────────────────────────────────────────────────

  /**
   * Called once after the full dependency graph has settled.
   * Reads the current availability for every resource in a single pass
   * and syncs the results atomically into Redux.
   */
  public onNetworkAvailabilityEvaluationComplete(): void {
    Log.d(
      NetworkAvailabilityBridge.TAG,
      "onNetworkAvailabilityEvaluationComplete() syncing all resources"
    );

    this.syncAllResources();
  }

  // ──────────────────────────────────────────────────────────────────
  // Internal helpers
  // ──────────────────────────────────────────────────────────────────

  /**
   * Reads the current availability for every resource from the domain
   * service and dispatches it into Redux.
   */
  private syncAllResources(): void {
    Log.d(
      NetworkAvailabilityBridge.TAG,
      "syncAllResources() syncing Redux with current graph state"
    );

    for (const resource of NetworkAvailabilityBridge.ALL_RESOURCES) {
      const availability: INetworkResourceAvailability =
        this.mService.getAvailability(resource);

      this.mDispatch(
        resourceAvailabilityChanged({
          resource,
          isAvailable: availability.isAvailable,
          failureReason: availability.failureReason
        })
      );
    }
  }
}

export { NetworkAvailabilityBridge };
